import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';

function BlogNavbar() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-background-50/98 backdrop-blur-md border-b border-secondary-200/60' : 'bg-background-50/90 backdrop-blur-md border-b border-secondary-100/40'
      }`}
    >
      <div className="w-full px-6 md:px-10">
        <div className="flex items-center justify-between h-16">
          <Link
            to="/"
            className="text-foreground-900 font-bold text-xl tracking-tight whitespace-nowrap hover:text-primary-500 transition-colors duration-200"
          >
            itaobuy
          </Link>
          <div className="flex items-center gap-6">
            <Link
              to="/"
              className="text-sm text-foreground-700 hover:text-foreground-900 transition-colors duration-200 whitespace-nowrap"
            >
              Home
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center px-5 py-2 rounded-[10px] bg-foreground-950 text-background-50 text-sm font-medium hover:bg-foreground-800 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              buy it now
            </a>
          </div>
        </div>
      </div>
    </nav>
  );
}

function BlogFooter() {
  const { t } = useTranslation();

  return (
    <footer className="bg-foreground-950 pt-16 pb-8">
      <div className="w-full px-6 md:px-10">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-8">
          <h3 className="text-2xl font-bold text-white">itaobuy</h3>
          <Link
            to="/"
            className="text-white/60 text-sm hover:text-white transition-colors duration-200 whitespace-nowrap"
          >
            {t('footer.back_home', 'Back to Home')}
          </Link>
        </div>
        <div className="border-t border-white/10 pt-6">
          <p className="text-white/40 text-xs text-center">
            {t('footer.copyright', '© 2026 itaobuy.boats. All rights reserved.')}
          </p>
        </div>
      </div>
    </footer>
  );
}

export default function BlogPage() {
  const { t } = useTranslation();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://itaobuy.boats';
  const logoUrl = 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/62f99c0415358bd5388d5ed1df9ac1ff.jpeg';

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "What Is ItaobuySpreadsheet? The Ultimate Product Sourcing System (2026 Guide)",
    "description": "Discover how ItaobuySpreadsheet helps e-commerce sellers streamline product research, compare suppliers, and scale their business with a structured, data-driven spreadsheet system.",
    "author": {
      "@type": "Person",
      "name": "Marcus Chen",
      "jobTitle": "E-commerce Entrepreneur"
    },
    "datePublished": "2026-06-27",
    "dateModified": "2026-06-27",
    "image": "https://readdy.ai/api/search-image?query=Clean%20modern%20workspace%20with%20laptop%20displaying%20colorful%20spreadsheet%20data%20and%20analytics%20charts%20on%20light%20wooden%20desk%20minimalist%20office%20setup%20warm%20natural%20lighting%20professional%20ecommerce%20atmosphere%20bright%20airy%20composition&width=1200&height=720&seq=blog-hero-image-v2&orientation=landscape",
    "publisher": {
      "@type": "Organization",
      "name": "Itaobuy Spreadsheet",
      "logo": {
        "@type": "ImageObject",
        "url": logoUrl
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${siteUrl}/itaobuyspreadsheet-guide`
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      {/* Schema.org Article */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <BlogNavbar />
      <main>
        {/* Article Hero */}
        <section className="relative pt-24 pb-16 md:pt-32 md:pb-20 bg-background-50 overflow-hidden">
          {/* Decorative blur */}
          <div className="absolute top-20 right-[10%] w-[400px] h-[400px] rounded-full bg-accent-50/50 blur-[100px] pointer-events-none"></div>

          <div className="w-full px-6 md:px-10 flex flex-col lg:flex-row items-center gap-10 lg:gap-20 relative z-10">
            {/* Left - Text */}
            <div className="w-full lg:w-[40%]">
              <Link
                to="/"
                className="inline-flex items-center gap-1 text-sm text-foreground-500 hover:text-primary-500 transition-colors duration-200 mb-6"
              >
                <i className="ri-arrow-left-line"></i>
                {t('back_to_home', '← Back to Home')}
              </Link>
              <h1 className="text-4xl md:text-5xl font-bold text-foreground-950 leading-[1.15] tracking-tight">
                {t('h1_line1', 'What Is ItaobuySpreadsheet?')}
                <br />
                {t('h1_line2', 'The 2026 Sourcing Guide')}
              </h1>
              <p className="mt-4 text-sm text-foreground-500">
                {t('meta_info', 'By Itaobuy Team · Updated June 2026')}
              </p>
              <p className="mt-4 text-base text-foreground-600 leading-relaxed max-w-lg">
                {t('meta_description')}
              </p>
            </div>

            {/* Right - Featured Image */}
            <div className="w-full lg:w-[60%]">
              <div className="rounded-3xl overflow-hidden bg-white">
                <img
                  src="https://readdy.ai/api/search-image?query=Clean%20modern%20workspace%20with%20laptop%20displaying%20colorful%20spreadsheet%20data%20and%20analytics%20charts%20on%20light%20wooden%20desk%20minimalist%20office%20setup%20warm%20natural%20lighting%20professional%20ecommerce%20atmosphere%20bright%20airy%20composition&width=1200&height=720&seq=blog-hero-image-v2&orientation=landscape"
                  alt="ItaobuySpreadsheet Guide"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Article Content - Introduction */}
        <section className="py-16 md:py-20 bg-white">
          <div className="w-full px-6 md:px-10 flex flex-col lg:flex-row gap-12 lg:gap-16">
            <div className="w-full lg:w-[45%]">
              <span className="inline-block px-3 py-1 rounded-md bg-secondary-200 text-xs font-medium text-foreground-600 mb-6">
                {t('intro_label', 'Introduction')}
              </span>
              <h2 className="text-3xl md:text-[44px] font-bold text-foreground-950 leading-[1.15]">
                {t('intro_title_line1', 'The Complete Guide to')}
                <br />
                {t('intro_title_line2', 'ItaobuySpreadsheet')}
                <br />
                {t('intro_title_line3', 'Product Sourcing')}
              </h2>
              <p className="mt-8 text-base text-foreground-600 leading-relaxed font-light">
                {t('intro_text')}
              </p>
            </div>

            <div className="w-full lg:w-[50%]">
              <div className="rounded-[32px] bg-primary-500 p-10 md:p-12">
                <blockquote className="text-background-50 text-lg leading-relaxed font-light">
                  "{t('quote')}"
                </blockquote>
                <div className="mt-6 flex items-center gap-3">
                  <img
                    src="https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20east%20asian%20male%20entrepreneur%20with%20glasses%20on%20clean%20white%20studio%20background%20friendly%20confident%20smile%20modern%20business%20casual%20blazer%20sharp%20professional%20look&width=96&height=96&seq=blog-author-avatar-v2&orientation=squarish"
                    alt={t('author')}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <p className="text-background-50 font-medium text-sm">{t('author')}</p>
                    <p className="text-background-50/50 text-xs">{t('role')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SEO-Specific Long-form Content Section */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="w-full px-6 md:px-10 max-w-3xl mx-auto">
            <article className="prose-custom">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-6">
                How ItaobuySpreadsheet Transforms E-Commerce Product Sourcing
              </h2>
              <p className="text-foreground-600 leading-relaxed mb-6">
                Product sourcing is the backbone of every successful e-commerce operation. Without a reliable system to track, compare, and evaluate products, sellers waste countless hours on manual research that yields inconsistent results. <strong>ItaobuySpreadsheet</strong> addresses this fundamental challenge by providing a structured, spreadsheet-based framework that organizes every aspect of the sourcing workflow into a logical, easy-to-navigate system.
              </p>
              <p className="text-foreground-600 leading-relaxed mb-6">
                The core philosophy behind <strong>ItaobuySpreadsheet</strong> is simple: replace chaos with clarity. Instead of juggling multiple browser tabs, scattered notes, and disconnected supplier communications, sellers using <strong>ItaobuySpreadsheet</strong> consolidate all critical sourcing data into a single, unified spreadsheet structure. This approach not only saves time but dramatically reduces the risk of overlooking profitable opportunities or making costly mistakes.
              </p>

              <h3 className="text-xl font-bold text-foreground-950 mt-10 mb-4">
                The Problem with Traditional Product Sourcing Methods
              </h3>
              <p className="text-foreground-600 leading-relaxed mb-6">
                Most e-commerce sellers start their sourcing journey with basic tools like generic spreadsheets, bookmarks, and email threads. While these tools work for a handful of products, they quickly become overwhelming as the product catalog grows. Information gets lost, comparisons become inconsistent, and the entire workflow slows to a crawl. <strong>ItaobuySpreadsheet</strong> was designed specifically to solve these scaling problems by introducing a systematic approach that grows with your business.
              </p>
              <p className="text-foreground-600 leading-relaxed mb-6">
                Sellers who switch to <strong>ItaobuySpreadsheet</strong> typically report significant improvements in their sourcing efficiency. The pre-configured fields eliminate the guesswork of what data to track, while the logical layout ensures that every piece of information is exactly where you expect to find it. This is not just another spreadsheet — it is a complete <strong>product sourcing spreadsheet</strong> methodology refined through real-world e-commerce experience.
              </p>

              <h3 className="text-xl font-bold text-foreground-950 mt-10 mb-4">
                Key Components of the ItaobuySpreadsheet System
              </h3>
              <p className="text-foreground-600 leading-relaxed mb-6">
                The <strong>ItaobuySpreadsheet</strong> system is built around several interconnected components that work together to create a seamless sourcing workflow. The <strong>product sourcing spreadsheet</strong> includes dedicated sections for supplier information, pricing data, shipping costs, profit calculations, and quality assessments. Each section is designed to capture the specific data points that matter most for making informed sourcing decisions.
              </p>
              <p className="text-foreground-600 leading-relaxed mb-6">
                One of the standout features of <strong>ItaobuySpreadsheet</strong> is the <strong>supplier comparison spreadsheet</strong> module. This allows sellers to evaluate multiple suppliers side by side using consistent criteria, making it easy to identify the best options for any given product. The comparison framework accounts for factors like unit pricing, minimum order quantities, shipping timelines, and quality ratings, providing a comprehensive view of each supplier relationship.
              </p>

              <h3 className="text-xl font-bold text-foreground-950 mt-10 mb-4">
                Who Benefits Most from ItaobuySpreadsheet?
              </h3>
              <p className="text-foreground-600 leading-relaxed mb-6">
                <strong>ItaobuySpreadsheet</strong> is designed for a broad range of e-commerce professionals. <strong>Dropshippers</strong> benefit from the system's ability to track products across multiple suppliers, while <strong>Amazon sellers</strong> appreciate the structured approach to profit margin analysis. <strong>Shopify store owners</strong> use <strong>ItaobuySpreadsheet</strong> to manage their product catalogs more efficiently, and <strong>import businesses</strong> rely on the system to coordinate complex international sourcing operations.
              </p>
              <p className="text-foreground-600 leading-relaxed mb-6">
                Whether you are managing 50 products or 5,000, <strong>ItaobuySpreadsheet</strong> provides the organizational framework needed to scale your sourcing operations without adding complexity. The <strong>china sourcing spreadsheet</strong> capabilities make it particularly valuable for sellers who source products from international markets, where the complexity of supplier relationships demands a more structured approach.
              </p>

              <h3 className="text-xl font-bold text-foreground-950 mt-10 mb-4">
                Getting Started with ItaobuySpreadsheet
              </h3>
              <p className="text-foreground-600 leading-relaxed mb-6">
                Getting started with <strong>ItaobuySpreadsheet</strong> is straightforward. The system is designed to be intuitive enough for beginners while powerful enough for experienced sellers. New users can begin by importing their existing product data into the structured format, while advanced users can customize the fields to match their specific workflow requirements. The <strong>dropshipping spreadsheet system</strong> adapts to your needs rather than forcing you into a rigid framework.
              </p>
              <p className="text-foreground-600 leading-relaxed mb-6">
                The learning curve for <strong>ItaobuySpreadsheet</strong> is minimal by design. Most users report becoming productive within their first hour of use, thanks to the logical layout and clear labeling of all fields and sections. Regular updates and community feedback ensure that the system continues to evolve in response to real seller needs, making <strong>ItaobuySpreadsheet</strong> a living tool that improves over time.
              </p>
            </article>
          </div>
        </section>

        {/* Benefits Cards */}
        <section className="py-16 md:py-20 bg-white">
          <div className="w-full px-6 md:px-10">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 rounded-full border border-secondary-300 text-xs font-medium text-foreground-600 mb-6">
                {t('benefits_label', 'Key Benefits')}
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950">
                {t('benefits_title')}
              </h2>
              <p className="mt-4 text-foreground-600 text-base max-w-xl mx-auto">
                {t('benefits_subtitle')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {[0, 1, 2].map((i) => (
                <div key={i} className="rounded-2xl bg-secondary-50 p-8">
                  <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center mb-5">
                    <i className={`text-xl text-primary-600 ${
                      i === 0 ? 'ri-folder-line' : i === 1 ? 'ri-scales-3-line' : 'ri-line-chart-line'
                    }`}></i>
                  </div>
                  <h3 className="text-lg font-bold text-foreground-900 mb-3">
                    {t(`benefit_cards.${i}.title`)}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {t(`benefit_cards.${i}.desc`)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Blog CTA */}
        <section className="relative h-[500px] md:h-[550px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20minimalist%20creative%20workspace%20with%20laptop%20and%20product%20samples%20on%20light%20wood%20desk%20soft%20natural%20afternoon%20sunlight%20from%20window%20clean%20modern%20aesthetic%20cozy%20atmosphere%20with%20subtle%20green%20plants&width=1600&height=900&seq=blog-cta-bg-v2&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
          <div className="relative z-10 text-center px-6 w-full max-w-2xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white tracking-wide">
              {t('cta_title', 'GET THE FULL SPREADSHEET')}
            </h2>
            <p className="mt-4 text-white/80 text-base leading-relaxed">
              {t('cta_subtitle_line1')}
              <br />
              {t('cta_subtitle_line2')}
            </p>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center mt-8 px-8 py-4 rounded-full bg-foreground-950 text-background-50 font-semibold text-sm tracking-wide hover:bg-foreground-800 transition-all duration-200 whitespace-nowrap cursor-pointer relative"
            >
              <span className="absolute left-0 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-accent-500 flex items-center justify-center -ml-1">
                <i className="ri-shopping-bag-line text-white text-lg"></i>
              </span>
              <span className="pl-10 pr-2">{t('cta_button', 'ACCESS ITAOBUY SHEET NOW')}</span>
              <i className="ri-arrow-right-up-line text-lg"></i>
            </a>
          </div>
        </section>
      </main>
      <BlogFooter />
    </div>
  );
}